import { useState } from "react";
import { ACTRA_CONTRACT_ADDRESS } from "@/config/actra";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { PreviewTag } from "@/components/kit";
import { demoProofs } from "@/data/demoProofs";
import { demoAgents } from "@/data/demoAgents";
import { demoTasks } from "@/data/demoTasks";

const TABS = ["VERIFIED ACTIONS", "AGENTS", "TASKS", "PROOFS", "SETTLEMENTS", "NETWORK"];

const Grid = ({ cols, head, rows }) => (
    <div className="overflow-x-auto border border-white/[0.08]">
        <table className="w-full min-w-[640px] text-left">
            <thead>
                <tr className="border-b border-white/[0.08] bg-[#07111F]">
                    {head.map((h) => (
                        <th key={h} className="label-tech px-5 py-4 font-medium">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i} className="border-b border-white/[0.05] transition-colors last:border-0 hover:bg-[#0A1627]">
                        {r.map((c, j) => (
                            <td key={j} className={`px-5 py-4 font-mono text-[11px] tracking-[0.08em] ${j === 0 ? "text-ink" : "text-mute"} ${cols?.[j] || ""}`}>
                                {c}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const Explorer = () => {
    const [tab, setTab] = useState(0);

    return (
        <>
            <PageHeader
                index="07"
                eyebrow="NETWORK EXPLORER"
                title={<>ACTRA <span className="text-[#1677FF]">EXPLORER</span></>}
                description="Verified actions, agents, tasks, proofs, settlements and network state — one surface. Real Arc indexing arrives with the contract launch."
            >
                <div className="mt-8">
                    <PreviewTag>PROTOCOL PREVIEW — NO REAL NETWORK ACTIVITY</PreviewTag>
                </div>
            </PageHeader>

            <section className="section">
                <div className="shell">
                    <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Explorer sections" data-testid="explorer-tabs">
                        {TABS.map((t, i) => (
                            <button
                                key={t}
                                role="tab"
                                aria-selected={tab === i}
                                data-testid={`explorer-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setTab(i)}
                                className={`shrink-0 border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                                    tab === i
                                        ? "border-[#1677FF] bg-[#1677FF]/15 text-ink"
                                        : "border-white/10 bg-[#0A1627] text-mute hover:border-[#1677FF]/50 hover:text-ink"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <Reveal delay={0.05} className="mt-8">
                        {tab === 0 && (
                            <Grid
                                cols={["text-[#31D8FF]"]}
                                head={["PROOF ID", "TASK", "AGENT", "STATUS", "SETTLEMENT"]}
                                rows={demoProofs.map((p) => [p.proofId, p.task, p.agent, p.status, p.settlement])}
                            />
                        )}
                        {tab === 1 && (
                            <Grid
                                cols={["text-[#31D8FF]"]}
                                head={["AGENT", "TYPE", "STATUS", "REPUTATION", "VERIFIED ACTIONS", "SETTLED"]}
                                rows={demoAgents.map((a) => [a.handle, a.type, a.status, a.reputation.toFixed(1), a.verifiedActions.toLocaleString(), `${a.totalSettled.toLocaleString()} USDC`])}
                            />
                        )}
                        {tab === 2 && (
                            <Grid
                                cols={["text-[#31D8FF]"]}
                                head={["TASK", "TITLE", "CATEGORY", "REWARD", "STATUS"]}
                                rows={demoTasks.slice(0, 6).map((t) => [`#${t.id}`, t.title, t.category.toUpperCase(), `${t.reward} USDC`, t.status])}
                            />
                        )}
                        {tab === 3 && (
                            <Grid
                                cols={["text-[#31D8FF]", "hash-shimmer"]}
                                head={["PROOF ID", "HASH", "EVIDENCE", "REPUTATION"]}
                                rows={demoProofs.map((p) => [p.proofId, p.hash, `${p.evidence.length} ITEMS`, p.reputation])}
                            />
                        )}
                        {tab === 4 && (
                            <div className="space-y-5">
                                <PreviewTag>EXAMPLE ONLY — ECONOMICS NOT FINALIZED</PreviewTag>
                                <Grid
                                    cols={["text-[#31D8FF]"]}
                                    head={["AGENT", "TASK", "AMOUNT", "ASSET", "STATE"]}
                                    rows={demoAgents.flatMap((a) => a.settlements.map((s) => [a.handle, s.task, String(s.amount), s.asset, "SETTLEMENT READY"]))}
                                />
                            </div>
                        )}
                        {tab === 5 && (
                            <div className="grid grid-cols-2 gap-px border border-white/[0.08] bg-white/[0.06] md:grid-cols-4">
                                {[
                                    ["NETWORK", "ARC MAINNET"],
                                    ["CHAIN ID", "5042"],
                                    ["GAS ASSET", "USDC"],
                                    ["FINALITY", "INSTANT"],
                                    ["EVM", "COMPATIBLE"],
                                    ["CONTRACT", ACTRA_CONTRACT_ADDRESS ? `${ACTRA_CONTRACT_ADDRESS.slice(0, 6)}…${ACTRA_CONTRACT_ADDRESS.slice(-4)}` : "COMING SOON"],
                                    ["BLOCK HEIGHT", "— AVAILABLE AT LAUNCH"],
                                    ["TPS", "— AVAILABLE AT LAUNCH"],
                                ].map(([k, v]) => (
                                    <div key={k} className="bg-[#0A1627] p-5">
                                        <p className="label-tech">{k}</p>
                                        <p className="mt-2 font-mono text-xs font-semibold text-ink md:text-sm">{v}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Reveal>

                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-mute/60" data-testid="explorer-note">
                        NETWORK INFORMATION — NO LIVE BLOCK HEIGHT OR TPS IS DISPLAYED. REAL ARC INDEXING IS PREPARED FOR LAUNCH.
                    </p>
                </div>
            </section>
        </>
    );
};

export default Explorer;
